import axios from 'axios';

const COMPANY_DETAILS_KEY = 'activeCompanyDetails';

/**
 * Fetches company details from company-master/view-companies.
 * Uses the first company in the list; optionally pass companyId to use that company when available.
 */
export async function fetchCurrentCompanyDetails(companyId) {
  const baseUrl = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
  try {
    const response = await axios.get(`${baseUrl}/company-master/view-companies`, {
      withCredentials: true,
    });
    const data = response.data;
    const list = Array.isArray(data) ? data : (data?.data ?? []);
    if (!list.length) return null;
    const raw = companyId != null && companyId !== ''
      ? list.find((c) => c.company_id === Number(companyId)) || list[0]
      : list[0];
    return normalizeCompanyDetails(raw);
  } catch (err) {
    console.error('Error fetching company details:', err);
    return null;
  }
}

/**
 * Normalize API response to a consistent shape (company details table fields).
 */
export function normalizeCompanyDetails(raw) {
  if (!raw) return null;
  return {
    company_name: raw.company_name ?? raw.name ?? '',
    locality: raw.locality ?? '',
    street: raw.street ?? '',
    place: raw.place ?? '',
    city: raw.city ?? '',
    state: raw.state ?? '',
    post_code: raw.post_code ?? raw.pinCode ?? '',
  };
}

/**
 * Get display strings for report headers and UI (name + address line 1 + line 2).
 */
export function getCompanyDisplayFromDetails(details) {
  if (!details || typeof details !== 'object') {
    return { companyName: '', addressLine1: '', addressLine2: '', locality: '' };
  }
  const companyName = details.company_name || '';
  const addressLine1 = [details.locality, details.street].filter(Boolean).join(', ');
  const addressLine2 = [details.place, details.city, details.state, details.post_code].filter(Boolean).join(', ');
  const locality = details.locality || '';
  return { companyName, addressLine1, addressLine2, locality };
}

/** If company name is longer than maxLength, return only the first word; otherwise return full name. */
const REPORT_NAME_MAX_LENGTH = 25;
export function getShortCompanyName(companyName, maxLength = REPORT_NAME_MAX_LENGTH) {
  const name = (companyName || '').trim();
  if (!name) return '';
  if (name.length <= maxLength) return name;
  const firstWord = name.split(/\s+/)[0] || name;
  return firstWord;
}

/**
 * Read stored company details from localStorage (fallback when Redux not yet hydrated).
 */
export function getStoredCompanyDetails() {
  try {
    const stored = localStorage.getItem(COMPANY_DETAILS_KEY);
    if (!stored) return null;
    return JSON.parse(stored);
  } catch {
    return null;
  }
}

export function setStoredCompanyDetails(details) {
  if (details) {
    localStorage.setItem(COMPANY_DETAILS_KEY, JSON.stringify(details));
  } else {
    localStorage.removeItem(COMPANY_DETAILS_KEY);
  }
}
